import { useEffect, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { name: "Ana Sayfa", path: "/", testid: "nav-link-ana-sayfa" },
  { name: "Kurumsal", path: "/kurumsal", testid: "nav-link-kurumsal" },
  { name: "Faaliyet Alanları", path: "/faaliyet-alanlari", testid: "nav-link-faaliyet-alanlari" },
  { name: "Proje Yaklaşımımız", path: "/proje-yaklasimimiz", testid: "nav-link-proje-yaklasimimiz" },
  { name: "Gayrimenkul Geliştirme", path: "/gayrimenkul-gelistirme", testid: "nav-link-gayrimenkul-gelistirme" },
  { name: "İletişim", path: "/iletisim", testid: "nav-link-iletisim" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToContact = () => {
    setMenuOpen(false);
    navigate("/iletisim");
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-navy/10"
          : "bg-white/80 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-[72px] flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          data-testid="site-logo"
          className="group flex items-center gap-3 shrink-0"
        >
          <div className="w-9 h-9 border border-navy flex items-center justify-center font-heading font-semibold text-navy text-[13px] tracking-tighter">
            1001
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-heading font-medium text-[15px] text-navy tracking-tight">
              1001 Mimarlık İnşaat
            </span>
            <span className="mt-1 text-[10px] tracking-[0.25em] uppercase text-anthracite/60 font-body">
              ve Ticaret A.Ş.
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav
          data-testid="desktop-nav"
          className="hidden lg:flex items-center gap-1"
        >
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              end={link.path === "/"}
              data-testid={link.testid}
              className={({ isActive }) =>
                `relative px-3 py-2 text-[13px] font-medium tracking-wide transition-colors ${
                  isActive
                    ? "text-navy"
                    : "text-anthracite/80 hover:text-navy"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative inline-block">
                  {link.name}
                  <span
                    className={`absolute left-0 -bottom-1 h-[2px] bg-gold transition-all duration-300 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center">
          <button
            data-testid="cta-iletisim-button"
            onClick={goToContact}
            className="group relative inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-[12.5px] font-medium tracking-wider uppercase transition-all duration-300 hover:bg-gold hover:text-white border border-navy hover:border-gold"
          >
            <span>Bizimle İletişime Geçin</span>
            <span className="w-6 h-[1px] bg-white/60 group-hover:bg-white transition-colors" />
          </button>
        </div>

        {/* Mobile menu button */}
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-navy/20 text-navy"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Menüyü aç/kapat"
        >
          {menuOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Mobile menu drawer */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 border-t border-navy/10 bg-white ${
          menuOpen ? "max-h-[520px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="px-6 py-6 flex flex-col gap-1">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              end={link.path === "/"}
              onClick={() => setMenuOpen(false)}
              data-testid={`mobile-${link.testid}`}
              className={({ isActive }) =>
                `flex items-center justify-between py-3 border-b border-navy/5 text-[14px] tracking-wide ${
                  isActive
                    ? "text-navy font-medium"
                    : "text-anthracite/80"
                }`
              }
            >
              <span>{link.name}</span>
              <span className="text-gold text-xs">→</span>
            </NavLink>
          ))}
          <button
            data-testid="mobile-cta-iletisim-button"
            onClick={goToContact}
            className="mt-6 w-full bg-navy text-white py-3 text-[12.5px] tracking-widest uppercase font-medium"
          >
            Bizimle İletişime Geçin
          </button>
        </nav>
      </div>
    </header>
  );
}
